import { useState } from "react";
import useSWR from "swr";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Cookie, CheckCircle, Warning, ArrowSquareOut, Download } from "@phosphor-icons/react";
import { toast } from "sonner";

const fetcher = (url) => api.get(url).then((r) => r.data);

export default function AmazonCookiesCard() {
    const { data: status, mutate, isLoading } = useSWR("/connectors/amazon/cookies/status", fetcher, {
        refreshInterval: 10000,
    });
    const [probing, setProbing] = useState(false);
    const [probeResult, setProbeResult] = useState(null);
    const [diagnosing, setDiagnosing] = useState(false);
    const [diagnoseResult, setDiagnoseResult] = useState(null);
    const [diagnoseYear, setDiagnoseYear] = useState(new Date().getFullYear());

    const configured = !!status?.configured;
    const cookieCount = status?.cookies_count || 0;
    const authMode = status?.auth_mode || "playwright";

    const probe = async () => {
        setProbing(true);
        setProbeResult(null);
        try {
            const { data } = await api.post("/connectors/amazon/cookies/probe");
            setProbeResult(data);
            if (data.signed_in) {
                toast.success(`Cookies OK — ${data.order_count_hint} commandes visibles`);
            } else {
                toast.error("Cookies expirés — reconnectez-vous à amazon.fr puis re-poussez-les");
            }
        } catch (e) {
            toast.error(e.response?.data?.detail || "Test échoué");
        } finally {
            setProbing(false);
        }
    };

    const diagnose = async () => {
        setDiagnosing(true);
        setDiagnoseResult(null);
        try {
            const { data } = await api.post("/connectors/amazon/cookies/diagnose", {
                year: diagnoseYear,
            });
            setDiagnoseResult(data);
            if (data.parser_orders > 0) {
                toast.success(`Parser OK : ${data.parser_orders} commandes détectées`);
            } else if (data.order_ids_in_html > 0) {
                toast.error(`HTML reçu (${data.order_ids_in_html} IDs trouvés) mais parser à ${data.parser_orders}`);
            } else {
                toast.error("Aucun order-ID dans le HTML reçu — voir détails ci-dessous");
            }
        } catch (e) {
            toast.error(e.response?.data?.detail || "Diagnostic échoué");
        } finally {
            setDiagnosing(false);
        }
    };

    return (
        <Card className="ec-card" data-testid="amazon-cookies-card">
            <CardContent className="p-6 space-y-4">
                <div className="flex items-start justify-between gap-3">
                    <div>
                        <h3 className="font-display text-lg font-bold tracking-tight flex items-center gap-2">
                            <Cookie size={18} weight="duotone" />
                            Amazon — Mode navigateur (extension Chrome)
                        </h3>
                        <p className="text-xs text-muted-foreground mt-1">
                            Le scraping se fait directement dans <strong>ton Chrome</strong> avec
                            ta vraie session — Amazon te voit comme un humain (pas d&apos;anti-bot).
                            L&apos;extension récupère tes factures et les envoie automatiquement
                            à ScanBox.
                        </p>
                    </div>
                    <div className="shrink-0">
                        {configured ? (
                            <Badge variant="outline" className="text-emerald-700 border-emerald-200 text-xs">
                                <CheckCircle size={12} weight="fill" className="mr-1" />
                                {cookieCount} cookies
                            </Badge>
                        ) : (
                            <Badge variant="outline" className="text-muted-foreground text-xs">
                                Non configuré
                            </Badge>
                        )}
                    </div>
                </div>

                {!isLoading && (
                    <Alert>
                        <AlertDescription className="text-xs space-y-2">
                            <div className="font-semibold">Installation rapide (5 min) — extension v1.2.0 (Amazon)</div>
                            <ol className="list-decimal pl-4 space-y-1">
                                <li>
                                    <a
                                        href="/scanbox-extension.zip"
                                        download
                                        className="text-primary underline inline-flex items-center gap-1"
                                        data-testid="amazon-cookies-download-extension"
                                    >
                                        Télécharger l’extension v1.2.0 <Download size={11} />
                                    </a>
                                    {" "}puis dézipper l’archive.
                                </li>
                                <li>
                                    Ouvrir <code>chrome://extensions</code>, activer le <strong>mode développeur</strong>,
                                    cliquer <strong>« Charger l’extension non empaquetée »</strong> et sélectionner le
                                    dossier <code>scanbox-extension</code>.
                                    Si déjà installée, cliquer <strong>« Actualiser »</strong> sur la carte ScanBox.
                                </li>
                                <li>
                                    Cliquer l’icône ScanBox, renseigner l’<strong>URL ScanBox</strong> et tes identifiants,
                                    puis sauvegarder.
                                </li>
                                <li>
                                    Aller sur <a href="https://www.amazon.fr/gp/your-account/order-history" target="_blank" rel="noreferrer" className="text-primary underline inline-flex items-center gap-1">Mes commandes Amazon <ArrowSquareOut size={11} /></a>
                                    {" "}— un <strong>badge vert ScanBox</strong> apparaît en haut à droite.
                                </li>
                                <li>
                                    Cliquer le badge vert <strong>« ScanBox · Cliquez pour scraper cette page »</strong>.
                                    L’extension parcourt automatiquement toutes les pages et envoie les factures.
                                </li>
                            </ol>
                            <div className="mt-2 text-[11px] text-muted-foreground">
                                💡 Astuce debug : ouvrir la console (F12) sur la page Amazon pour voir les logs
                                <code className="mx-1">[ScanBox]</code> détaillés.
                            </div>
                        </AlertDescription>
                    </Alert>
                )}

                {configured && (
                    <div className="space-y-3">
                        <div className="text-xs text-muted-foreground">
                            Dernier push&nbsp;: {status?.updated_at ? new Date(status.updated_at).toLocaleString("fr-FR") : "—"}
                            {" · "}
                            Mode&nbsp;: <code>{authMode}</code>
                        </div>
                        <div className="flex flex-wrap gap-2">
                            <Button
                                size="sm"
                                variant="outline"
                                onClick={probe}
                                disabled={probing}
                                data-testid="amazon-cookies-probe"
                            >
                                {probing ? "Test en cours…" : "Tester les cookies"}
                            </Button>
                            <details className="text-xs">
                                <summary className="cursor-pointer text-muted-foreground">Outils avancés (debug serveur)</summary>
                                <div className="flex flex-wrap items-center gap-2 mt-2">
                                    <Button
                                        size="sm"
                                        variant="outline"
                                        onClick={diagnose}
                                        disabled={diagnosing}
                                        data-testid="amazon-cookies-diagnose"
                                    >
                                        {diagnosing ? "Diagnostic…" : "🔬 Diagnostic HTML (obsolète — bloqué anti-bot)"}
                                    </Button>
                                    <select
                                        value={diagnoseYear}
                                        onChange={(e) => setDiagnoseYear(parseInt(e.target.value, 10))}
                                        className="text-xs px-2 py-1 border rounded"
                                        data-testid="amazon-cookies-diagnose-year"
                                    >
                                        {[0, 1, 2, 3, 4].map((d) => {
                                            const y = new Date().getFullYear() - d;
                                            return <option key={y} value={y}>{y}</option>;
                                        })}
                                    </select>
                                </div>
                                <p className="text-xs text-muted-foreground mt-1">
                                    Cet outil teste l&apos;ancien mode (scraping serveur). Amazon le bloque
                                    par anti-bot — c&apos;est normal et attendu. L&apos;extension Chrome
                                    contourne cette limite en faisant le scraping depuis ton navigateur.
                                </p>
                            </details>
                            <a
                                href="/scanbox-extension.zip"
                                download
                                className="inline-flex items-center text-xs text-muted-foreground hover:text-foreground underline"
                                data-testid="amazon-cookies-redownload"
                            >
                                Re-télécharger l&apos;extension
                            </a>
                        </div>
                        {probeResult && (
                            <Alert variant={probeResult.signed_in ? "default" : "destructive"}>
                                <AlertDescription className="text-xs">
                                    {probeResult.signed_in ? (
                                        <>
                                            <CheckCircle size={12} weight="fill" className="inline mr-1 text-emerald-600" />
                                            Connecté à <strong>{probeResult.marketplace}</strong>
                                            {" · "}
                                            {probeResult.order_count_hint} commandes visibles sur la 1ʳᵉ page.
                                        </>
                                    ) : (
                                        <>
                                            <Warning size={12} weight="fill" className="inline mr-1" />
                                            Cookies non valides (statut {probeResult.status_code}). Reconnectez-vous à amazon.fr puis re-poussez les cookies depuis l&apos;extension.
                                        </>
                                    )}
                                </AlertDescription>
                            </Alert>
                        )}
                        {diagnoseResult && (
                            <Alert>
                                <AlertDescription className="text-xs space-y-1">
                                    <div className="font-semibold mb-1">Diagnostic du parseur</div>
                                    <div>Client HTTP : <code>{diagnoseResult.http_client || "httpx"}</code></div>
                                    <div>Statut HTTP : <code>{diagnoseResult.status_code}</code> · URL finale : <code className="break-all">{diagnoseResult.final_url}</code></div>
                                    <div>{diagnoseResult.redirected_to_signin ? "⚠️ Redirigé vers la page de login Amazon" : "✅ Page commandes atteinte"}</div>
                                    <div>Cookies envoyés : <strong>{diagnoseResult.cookies_sent}</strong></div>
                                    {diagnoseResult.critical_cookies_present && (
                                        <div>Cookies critiques présents :{" "}
                                            {Object.entries(diagnoseResult.critical_cookies_present).map(([k, v]) => (
                                                <span key={k} className={v ? "text-emerald-700" : "text-red-700"}>
                                                    {v ? "✓" : "✗"} {k}{" "}
                                                </span>
                                            ))}
                                        </div>
                                    )}
                                    <div>Titre : <em>{diagnoseResult.title || "—"}</em></div>
                                    <div>HTML reçu : <strong>{diagnoseResult.html_length?.toLocaleString("fr-FR")} octets</strong></div>
                                    <div>Order IDs trouvés dans le HTML : <strong>{diagnoseResult.order_ids_in_html}</strong></div>
                                    <div>Commandes parsées par ScanBox : <strong>{diagnoseResult.parser_orders}</strong></div>
                                    <div>Liens popover : <strong>{diagnoseResult.popover_anchors}</strong> · Liens facture directs : <strong>{diagnoseResult.invoice_anchors}</strong></div>
                                    {diagnoseResult.first_order_ids?.length > 0 && (
                                        <div>Exemples d&apos;IDs : <code>{diagnoseResult.first_order_ids.join(", ")}</code></div>
                                    )}
                                    {diagnoseResult.bot_signals && (
                                        Object.values(diagnoseResult.bot_signals).some(Boolean) ? (
                                            <div className="mt-2 p-2 border-2 border-red-300 rounded bg-red-50 text-red-900">
                                                <div className="font-semibold">🤖 Signaux anti-bot détectés</div>
                                                <pre className="text-xs">{JSON.stringify(diagnoseResult.bot_signals, null, 2)}</pre>
                                            </div>
                                        ) : null
                                    )}
                                    {diagnoseResult.json_signals && (
                                        <div className="mt-2 text-xs">
                                            <div><strong>Sondes JSON dans le HTML</strong> (indique si Amazon embarque les commandes en JS) :</div>
                                            <pre className="bg-muted p-2 rounded">{JSON.stringify(diagnoseResult.json_signals, null, 2)}</pre>
                                        </div>
                                    )}
                                    {diagnoseResult.is_react_app && (
                                        <div className="mt-2 text-xs text-amber-700">
                                            ⚛️ Page React/SPA détectée — les commandes sont chargées dynamiquement
                                        </div>
                                    )}
                                    {diagnoseResult.url_variants && (
                                        <div className="mt-3 p-2 border rounded bg-amber-50">
                                            <div className="font-semibold text-amber-900 mb-1">⚡ Comparaison des 3 URLs Amazon (clé pour 200+ commandes)</div>
                                            <table className="text-xs border-collapse w-full">
                                                <thead>
                                                    <tr className="bg-amber-100">
                                                        <th className="text-left p-1 border">URL</th>
                                                        <th className="text-right p-1 border">HTML</th>
                                                        <th className="text-right p-1 border">Commandes (3p)</th>
                                                        <th className="text-left p-1 border">URL finale</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {Object.entries(diagnoseResult.url_variants).map(([k, v]) => (
                                                        <tr key={k}>
                                                            <td className="p-1 border font-mono">{k}</td>
                                                            <td className="p-1 border text-right">{v.html_length?.toLocaleString("fr-FR") || "—"}</td>
                                                            <td className="p-1 border text-right font-bold">{v.unique_order_ids_3pages ?? "—"}</td>
                                                            <td className="p-1 border text-xs break-all" style={{ maxWidth: "200px" }}><code>{v.final_url?.slice(0, 80) || "—"}</code></td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    )}
                                    <details className="mt-2">
                                        <summary className="cursor-pointer text-primary">Voir les sélecteurs testés</summary>
                                        <pre className="text-xs bg-muted p-2 rounded mt-1 overflow-auto">
{JSON.stringify(diagnoseResult.selector_hits, null, 2)}
                                        </pre>
                                    </details>
                                    {diagnoseResult.sample_card_text && (
                                        <details className="mt-1">
                                            <summary className="cursor-pointer text-primary">Texte de la 1ʳᵉ commande détectée</summary>
                                            <pre className="text-xs bg-muted p-2 rounded mt-1 overflow-auto whitespace-pre-wrap">{diagnoseResult.sample_card_text}</pre>
                                        </details>
                                    )}
                                    <details className="mt-1">
                                        <summary className="cursor-pointer text-primary">Aperçu HTML (4 ko)</summary>
                                        <pre className="text-xs bg-muted p-2 rounded mt-1 overflow-auto max-h-64">{diagnoseResult.html_head}</pre>
                                    </details>
                                </AlertDescription>
                            </Alert>
                        )}
                    </div>
                )}
            </CardContent>
        </Card>
    );
}
